import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';
import type { OpportunityAction, OpportunityItem, OpportunityStatus } from '../types';

// Consulting layer (spec 11.6): the scored options as a live, re-ranking ledger plus
// loop-generated triage candidates. Reads /api/opportunities; promote/pause/kill via the
// disposition endpoint. Dollar figures are modeled estimates on proxy data (basis), so the
// copy says "approximately".

const STATUS_TAG: Record<OpportunityStatus, string> = {
  active: 'teal',
  candidate: 'blue',
  paused: 'gold',
  killed: 'muted',
  blocked: 'red',
};

// Which actions the operator can take from each status (mirrors the server lifecycle).
const ACTIONS_FOR: Record<OpportunityStatus, OpportunityAction[]> = {
  candidate: ['promote', 'kill'],
  active: ['pause', 'kill'],
  paused: ['promote', 'kill'],
  killed: ['promote'],
  blocked: [],
};

const ACTION_LABEL: Record<OpportunityAction, string> = {
  promote: 'Promote',
  pause: 'Pause',
  kill: 'Kill',
};

function dollars(range: [number, number]): string {
  return `~$${range[0]}–$${range[1]}M`;
}

export default function OpportunityLedger() {
  const qc = useQueryClient();
  const ledger = useQuery({ queryKey: ['opportunities'], queryFn: api.opportunities });
  const [err, setErr] = useState<string | null>(null);

  const dispose = useMutation({
    mutationFn: ({ id, action }: { id: string; action: OpportunityAction }) =>
      api.opportunityDisposition(id, action),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ['opportunities'] });
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Disposition failed'),
  });

  if (ledger.isLoading) return <div className="small faint">Loading opportunity ledger…</div>;
  if (ledger.isError || !ledger.data) return <div className="small faint">Opportunity ledger unavailable.</div>;

  const { opportunities, candidates, coverage_note } = ledger.data;

  return (
    <div>
      <div className="ph">Consulting · Live Opportunity Ledger</div>
      <div className="h2">{opportunities.length} scored opportunities · {candidates.length} triage candidate{candidates.length === 1 ? '' : 's'}</div>
      <p className="lead">
        Importance × Readiness, re-ranking as data connects. {coverage_note}
      </p>
      {err && <div className="login-err" style={{ marginBottom: 12 }}>{err}</div>}

      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="qhead"><span className="qdot" style={{ background: 'var(--teal)' }} />Scored slate</div>
        <table>
          <thead>
            <tr>
              <th>Opportunity</th>
              <th>Tier</th>
              <th className="center">Priority</th>
              <th className="center">Coverage</th>
              <th className="center">$ at stake</th>
              <th>Status</th>
              <th className="center">Action</th>
            </tr>
          </thead>
          <tbody>
            {opportunities.map((o: OpportunityItem) => (
              <tr key={o.id}>
                <td>
                  <div style={{ fontWeight: 500, color: 'var(--ink)' }}>{o.name}</div>
                  <div className="small faint">{o.anchor}</div>
                </td>
                <td className="small">{o.tier}</td>
                <td className="center mono">{o.priority.toFixed(1)}</td>
                <td className="center"><span className="tag muted">{o.data_coverage}</span></td>
                <td className="center small">{dollars(o.dollar_at_stake_usd_m)}</td>
                <td><span className={`tag ${STATUS_TAG[o.status]}`}>{o.status}</span></td>
                <td className="center">
                  <DispositionButtons
                    status={o.status}
                    disabled={dispose.isPending}
                    onAct={(action) => dispose.mutate({ id: o.id, action })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {candidates.length > 0 && (
        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--blue)' }} />Triage candidates · from repeated breaches</div>
          <table>
            <thead>
              <tr>
                <th>Candidate</th>
                <th className="center">Breaches</th>
                <th className="center">Would-be severity</th>
                <th className="center">$ at stake</th>
                <th>Status</th>
                <th className="center">Action</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => (
                <tr key={c.id}>
                  <td style={{ color: 'var(--ink)' }}>{c.name}</td>
                  <td className="center mono">{c.breach_count}</td>
                  <td className="center"><span className="tag amber">{c.would_be_severity}</span></td>
                  <td className="center small">{dollars(c.dollar_at_stake_usd_m)}</td>
                  <td><span className={`tag ${STATUS_TAG[c.status]}`}>{c.status}</span></td>
                  <td className="center">
                    <DispositionButtons
                      status={c.status}
                      disabled={dispose.isPending}
                      onAct={(action) => dispose.mutate({ id: c.id, action })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DispositionButtons({
  status,
  disabled,
  onAct,
}: {
  status: OpportunityStatus;
  disabled: boolean;
  onAct: (action: OpportunityAction) => void;
}) {
  const actions = ACTIONS_FOR[status];
  if (actions.length === 0) return <span className="small faint">—</span>;
  return (
    <div className="row" style={{ gap: 6, justifyContent: 'center' }}>
      {actions.map((action) => (
        <button
          key={action}
          type="button"
          className="btn ghost"
          style={{ minHeight: 'auto', padding: '5px 10px', fontSize: 12 }}
          disabled={disabled}
          onClick={() => onAct(action)}
        >
          {ACTION_LABEL[action]}
        </button>
      ))}
    </div>
  );
}
